import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import PageHeader from '@components/common/PageHeader';
import FormSection from '@components/Form/FormSection/FormSection';
import Input from '@components/Form/Input/Input';
import Select from '@components/Form/Select/Select';
import Textarea from '@components/Form/Textarea/Textarea';
import Button from '@components/Button/Button';
import Icon from '@components/common/Icon';
import Toggle from '@components/common/Toggle';

import coursesService from '@/services/courses/coursesService';
import cohortsService, { type Day } from '@/services/cohorts/cohortsService';
import applicationsService from '@/services/applications/applicationsService';
import mentorsService from '@/services/mentors/mentorsService';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';
import AttendanceTab from './AttendanceTab';
import CodingTasksTab from './CodingTasksTab';
import EvaluationGrid from '@pages/EvaluationsPage/EvaluationGrid';
import SidebarModulesSelector from '@/components/Cohorts/SidebarModulesSelector';

interface CoreFormValues {
    course_id: string;
    cohort_name: string;
    description: string;
    start_date: string;
    registration_start: string;
    registration_end: string;
    duration: number;
    status: 'ACTIVE' | 'INACTIVE' | 'COMPLETED';
    handbook_path?: string;
    module_template_ids?: string[];
    student_portal_version: 'CLASSIC' | 'JOURNEY';
}

interface WeekFormValues {
    title: string;
    days: Day[];
}

const CohortDetailsPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t } = useTranslation(['cohort-create', 'cohorts']);

    const [mainTab, setMainTab] = useState<'config' | 'coding' | 'attendance' | 'evaluation'>('config');
    const [activeWeekTab, setActiveWeekTab] = useState<number>(1);
    const [openDays, setOpenDays] = useState<Record<number, boolean>>({ 1: true });
    const [visibilityUpdatingKey, setVisibilityUpdatingKey] = useState<string | null>(null);
    const [uploadingVideos, setUploadingVideos] = useState<Record<string, boolean>>({});

    // 1. Fetch Cohort core data
    const { data: cohortRes, isLoading: isLoadingCohort } = useQuery({
        queryKey: ['cohort', id],
        queryFn: () => cohortsService.getCohort(id!),
        enabled: !!id,
    });

    const cohort = cohortRes?.data;


    // 2. Fetch Cohort weekly schedule
    const { data: scheduleRes, isLoading: isLoadingSchedule } = useQuery({
        queryKey: ['cohort-schedule', id],
        queryFn: () => cohortsService.getSchedule(id!),
        enabled: !!id,
    });

    const scheduleList = scheduleRes?.data ?? [];

    // 3. Fetch courses list for core details editor
    const { data: coursesResponse } = useQuery({
        queryKey: ['courses-lookup'],
        queryFn: () => coursesService.getCourses(1, 100, 'ACTIVE'),
    });
    const courses = coursesResponse?.data?.data ?? [];
    const courseOptions = useMemo(() => courses.map(c => ({ value: c.id, label: c.course_name })), [courses]);

    // Core and schedule details remain editable at any time, even after the cohort starts.
    const isCoreLocked = false;

    // Form 1: Core details form
    const {
        control: coreControl,
        handleSubmit: handleCoreSubmit,
        reset: resetCore,
        setValue: setCoreValue,
        formState: { errors: coreErrors },
    } = useForm<CoreFormValues>({
        defaultValues: {
            course_id: '',
            cohort_name: '',
            description: '',
            start_date: '',
            registration_start: '',
            registration_end: '',
            duration: 30,
            status: 'INACTIVE',
            module_template_ids: [],
            student_portal_version: 'CLASSIC',
        },
    });

    // Populate core form when cohort details load
    useEffect(() => {
        if (cohort) {
            resetCore({
                course_id: cohort.course_id,
                cohort_name: cohort.cohort_name,
                description: cohort.description,
                start_date: cohort.start_date.substring(0, 10),
                registration_start: cohort.registration_start.substring(0, 10),
                registration_end: cohort.registration_end.substring(0, 10),
                duration: cohort.duration,
                status: cohort.status,
                handbook_path: cohort.handbook_path || '',
                module_template_ids: cohort.module_template_ids || [],
                student_portal_version: cohort.student_portal_version === 'JOURNEY' ? 'JOURNEY' : 'CLASSIC',
            });
        }
    }, [cohort, resetCore]);

    // Form 2: Active Week Schedule Form
    const {
        control: weekControl,
        handleSubmit: handleWeekSubmit,
        reset: resetWeek,
        setValue: setWeekValue,
        getValues: getWeekValues,
        trigger: triggerWeek,
        formState: { errors: weekErrors },
    } = useForm<WeekFormValues>({
        defaultValues: {
            title: '',
            days: [],
        },
    });

    // Fetch enrolled students
    const { data: studentsRes } = useQuery({
        queryKey: ['cohort-students', id],
        queryFn: () => cohortsService.getEnrolledStudents(id!),
        enabled: !!id,
    });
    const enrolledStudents = studentsRes?.data ?? [];
    
    // Hook form watched days (defined at top-level)
    const watchedDays = useWatch({ control: weekControl, name: 'days' }) || [];

    // Fetch all mentors for assignment dropdown
    const { data: mentorsRes } = useQuery({
        queryKey: ['all-mentors-for-assign'],
        queryFn: () => mentorsService.getMentors(1, 100),
    });
    const mentorsList = mentorsRes?.data?.data ?? [];

    // Mentor assignment mutation
    const assignMentorMutation = useMutation({
        mutationFn: (payload: { applicationId: string; mentorId: string }) =>
            applicationsService.assignMentor(payload.applicationId, payload.mentorId),
        onSuccess: () => {
            toast.success('Mentor assigned successfully');
            queryClient.invalidateQueries({ queryKey: ['cohort-students', id] });
        },
        onError: (err: any) => {
            toast.error(err.response?.data?.message || 'Failed to assign mentor');
        },
    });

    // Fetch the active week's days from fetched schedule array
    const activeWeekSchedule = useMemo(() => {
        return scheduleList.find(w => w.week_number === activeWeekTab);
    }, [scheduleList, activeWeekTab]);

    // Populate active week form when activeWeekSchedule loads or tab changes
    useEffect(() => {
        if (activeWeekSchedule) {
            resetWeek({
                title: activeWeekSchedule.title || '',
                days: activeWeekSchedule.days.map(d => ({
                    day_number: d.day_number,
                    date: d.date,
                    title: d.title || '',
                    description: d.description || '',
                    link: d.link || '',
                    recorded_video: d.recorded_video || '',
                    recorded_video_path: d.recorded_video_path || '',
                    recorded_video_name: d.recorded_video_name || '',
                    recordings: d.recordings || [],
                    class_time: d.class_time || '',
                    attachments: d.attachments || [],
                    is_active: d.is_active ?? true,
                    is_mentor_session: d.is_mentor_session ?? false,
                    student_links: d.student_links || [],
                })),
            });
        }
    }, [activeWeekSchedule, resetWeek]);

    // Live generated title preview for core form
    const watchedCohortName = useWatch({ control: coreControl, name: 'cohort_name' });
    const watchedStartDate = useWatch({ control: coreControl, name: 'start_date' });
    const watchedRegStart = useWatch({ control: coreControl, name: 'registration_start' });
    const watchedRegEnd = useWatch({ control: coreControl, name: 'registration_end' });

    const todayStr = useMemo(() => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }, []);

    const minRegEnd = useMemo(() => {
        return watchedRegStart || todayStr;
    }, [watchedRegStart, todayStr]);

    const minStartDate = useMemo(() => {
        if (!watchedRegEnd) return todayStr;
        try {
            const d = new Date(watchedRegEnd);
            d.setDate(d.getDate() + 1);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        } catch {
            return todayStr;
        }
    }, [watchedRegEnd, todayStr]);

    // Clearing values when previous dates are adjusted to be invalid
    useEffect(() => {
        if (watchedRegStart && watchedRegEnd && watchedRegStart > watchedRegEnd) {
            setCoreValue('registration_end', '');
            setCoreValue('start_date', '');
        }
    }, [watchedRegStart, watchedRegEnd, setCoreValue]);

    useEffect(() => {
        if (watchedRegEnd && watchedStartDate && watchedRegEnd >= watchedStartDate) {
            setCoreValue('start_date', '');
        }
    }, [watchedRegEnd, watchedStartDate, setCoreValue]);

    const generatedTitlePreview = useMemo(() => {
        const rawName = watchedCohortName || cohort?.cohort_name || cohort?.generated_cohort_title;
        if (!rawName) return cohort?.generated_cohort_title ?? '';
        let trimmed = rawName.trim();
        trimmed = trimmed.replace(/^(.+?)\s*\(\1\)$/i, '$1').trim();
        const match = trimmed.match(/^(.+?)\s*\((.+?)\)$/i);
        if (match && match[1].trim().toLowerCase() === match[2].trim().toLowerCase()) {
            trimmed = match[1].trim();
        }

        const dateToUse = watchedStartDate || cohort?.start_date;
        if (!dateToUse) return trimmed;
        try {
            const date = new Date(dateToUse);
            if (isNaN(date.getTime())) return trimmed;
            const month = date.toLocaleString('en-US', { month: 'long' });
            const year = date.getFullYear();
            const dateTag = `${month} ${year}`;

            const lowerNorm = trimmed.toLowerCase();
            const lowerTag = dateTag.toLowerCase();

            if (lowerNorm === lowerTag || lowerNorm.endsWith(`(${lowerTag})`) || lowerNorm.includes(lowerTag)) {
                return trimmed;
            }

            return `${trimmed} (${dateTag})`;
        } catch {
            return trimmed;
        }
    }, [watchedCohortName, watchedStartDate, cohort]);

    // Date displays next to day numbers
    const getDayDateLabel = (dayNum: number) => {
        const baseDateStr = watchedStartDate || cohort?.start_date;
        if (!baseDateStr) return '';
        try {
            const date = new Date(baseDateStr);
            if (isNaN(date.getTime())) return '';
            const offset = (activeWeekTab - 1) * 7 + (dayNum - 1);
            date.setDate(date.getDate() + offset);
            return date.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return '';
        }
    };

    const toggleDayAccordion = (dayNum: number) => {
        setOpenDays(prev => ({
            ...prev,
            [dayNum]: !prev[dayNum],
        }));
    };

    const addAttachment = (dayIdx: number) => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '*';
        fileInput.onchange = async (e: Event) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;

            const uploadToast = toast.loading(`Uploading "${file.name}"...`);
            try {
                const res = await cohortsService.uploadFile(file);
                if (res.data?.filePath) {
                    const fileObj = JSON.stringify({
                        filePath: res.data.filePath,
                        fileName: res.data.fileName || file.name,
                        fileSize: res.data.fileSize || 'N/A',
                    });
                    const current = getWeekValues(`days.${dayIdx}.attachments`) || [];
                    setWeekValue(`days.${dayIdx}.attachments`, [...current, fileObj], { shouldDirty: true });
                    void triggerWeek(`days.${dayIdx}.attachments`);
                    toast.success('File uploaded and attached successfully!', { id: uploadToast });
                } else {
                    toast.error('Upload failed: invalid server response', { id: uploadToast });
                }
            } catch (err: any) {
                const errMsg = err.response?.data?.message || err.message || 'File upload failed';
                toast.error(`Upload failed: ${errMsg}`, { id: uploadToast });
            }
        };
        fileInput.click();
    };

    const removeAttachment = (dayIdx: number, attachmentIdx: number) => {
        const current = getWeekValues(`days.${dayIdx}.attachments`) || [];
        setWeekValue(
            `days.${dayIdx}.attachments`,
            current.filter((_, idx) => idx !== attachmentIdx),
            { shouldDirty: true },
        );
        void triggerWeek(`days.${dayIdx}.attachments`);
    };

    // Mutations
    const updateCoreMutation = useMutation({
        mutationFn: (payload: CoreFormValues) => cohortsService.updateCohort(id!, payload),
        onSuccess: (res) => {
            toast.success(res.message || 'Cohort core details updated successfully');
            queryClient.invalidateQueries({ queryKey: ['cohort', id] });
            queryClient.invalidateQueries({ queryKey: ['cohort-schedule', id] }); // start_date update recalculates days dates
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
        },
        onError: (err: unknown) => {
            toast.error(getApiErrorMessage(err, 'Failed to update cohort details'));
        },
    });

    const updateWeekMutation = useMutation({
        mutationFn: (payload: WeekFormValues) =>
            cohortsService.updateScheduleWeek(id!, activeWeekTab, payload.days, payload.title),
        onSuccess: (res) => {
            toast.success(res.message || `Week ${activeWeekTab} schedule updated successfully`);
            queryClient.invalidateQueries({ queryKey: ['cohort-schedule', id] });
            resetWeek(getWeekValues());
        },
        onError: (err: unknown) => {
            toast.error(getApiErrorMessage(err, 'Failed to update week schedule'));
        },
    });

    // Visibility toggles are intentionally decoupled from the content form/lock:
    // admins can hide/show weeks and days at any time, even after the cohort starts.
    const updateVisibilityMutation = useMutation({
        mutationFn: (payload: { week_is_active?: boolean; days?: { day_number: number; is_active: boolean }[] }) =>
            cohortsService.updateScheduleVisibility(id!, activeWeekTab, payload),
        onSuccess: (res) => {
            toast.success(res.message || 'Visibility updated successfully');
            queryClient.invalidateQueries({ queryKey: ['cohort-schedule', id] });
        },
        onError: (err: unknown) => {
            toast.error(getApiErrorMessage(err, 'Failed to update visibility'));
        },
        onSettled: () => setVisibilityUpdatingKey(null),
    });

    const onCoreSubmit = (data: CoreFormValues) => {
        updateCoreMutation.mutate(data);
    };

    const onWeekSubmit = (data: WeekFormValues) => {
        const sanitizedData: WeekFormValues = {
            ...data,
            days: data.days.map(d => ({
                ...d,
                link: d.is_mentor_session ? 'https://placeholder-mentor-link' : d.link,
            }))
        };
        updateWeekMutation.mutate(sanitizedData);
    };

    const isLoading = isLoadingCohort || isLoadingSchedule;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-[400px]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--primary-color)]" />
            </div>
        );
    }

    return (
        <>
            <PageHeader
                title={cohort?.generated_cohort_title ?? 'Cohort Details'}
                breadcrumbItems={[
                    { label: t('cohorts:breadcrumb'), to: '/admin/dashboard' },
                    { label: t('cohorts:breadcrumb_cohort'), to: '/admin/cohorts' },
                    { label: cohort?.generated_cohort_title ?? 'Details' },
                ]}
            />
            <div className="flex flex-col px-4 pb-24 gap-6">
                {/* Main Tabs */}
                <div className="flex border-b border-gray-200 mb-2">
                    <button
                        type="button"
                        onClick={() => setMainTab('config')}
                        className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors cursor-pointer ${
                            mainTab === 'config'
                                ? 'border-[var(--primary-color)] text-[var(--primary-color)]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Configuration & Schedule
                    </button>
                    <button
                        type="button"
                        onClick={() => setMainTab('coding')}
                        className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors cursor-pointer ${
                            mainTab === 'coding'
                                ? 'border-[var(--primary-color)] text-[var(--primary-color)]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Coding Tasks
                    </button>
                    <button
                        type="button"
                        onClick={() => setMainTab('attendance')}
                        className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors cursor-pointer ${
                            mainTab === 'attendance'
                                ? 'border-[var(--primary-color)] text-[var(--primary-color)]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Attendance
                    </button>
                    <button
                        type="button"
                        onClick={() => setMainTab('evaluation')}
                        className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors cursor-pointer ${
                            mainTab === 'evaluation'
                                ? 'border-[var(--primary-color)] text-[var(--primary-color)]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Evaluation
                    </button>
                </div>

                {mainTab === 'config' && (
                    <>
                        {/* 1. Core Info Form */}
                        <form onSubmit={handleCoreSubmit(onCoreSubmit)} noValidate>
                    <FormSection
                        title={t('cohort-create:sections.basic_info')}
                        icon={<Icon name="formSection" className="w-[12px] 2xl:w-[14px] h-auto" alt="" />}
                        isOpen={true}
                        headerActions={
                            isCoreLocked ? (
                                <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg font-semibold">
                                    <span className="text-sm">🔒</span>
                                    Core Details Locked
                                </div>
                            ) : null
                        }
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Controller
                                name="course_id"
                                control={coreControl}
                                rules={{ required: t('cohort-create:validation.course_required') }}
                                render={({ field }) => (
                                    <Select
                                        label={t('cohort-create:fields.course_label')}
                                        placeholder="Select Course"
                                        options={courseOptions}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={coreErrors.course_id?.message}
                                        isDisabled={isCoreLocked}
                                        required
                                    />
                                )}
                            />

                            <Controller
                                name="cohort_name"
                                control={coreControl}
                                rules={{ required: t('cohort-create:validation.name_required') }}
                                render={({ field }) => (
                                    <Input
                                        label={t('cohort-create:fields.cohort_name_label')}
                                        placeholder={t('cohort-create:fields.cohort_name_placeholder')}
                                        error={coreErrors.cohort_name?.message}
                                        required
                                        isDisabled={isCoreLocked}
                                        {...field}
                                    />
                                )}
                            />

                            <div className="col-span-1 md:col-span-2">
                                <Input
                                    label={t('cohort-create:fields.generated_title_label')}
                                    value={generatedTitlePreview}
                                    disabled
                                    className="bg-gray-50 border-gray-200 text-gray-500 font-semibold"
                                />
                            </div>

                            <div className="col-span-1 md:col-span-2">
                                <Controller
                                    name="description"
                                    control={coreControl}
                                    rules={{ required: t('cohort-create:validation.description_required') }}
                                    render={({ field }) => (
                                        <Textarea
                                            label={t('cohort-create:fields.description_label')}
                                            placeholder={t('cohort-create:fields.description_placeholder')}
                                            rows={4}
                                            error={coreErrors.description?.message}
                                            required
                                            disabled={isCoreLocked}
                                            {...field}
                                            value={field.value || ''}
                                        />
                                    )}
                                />
                            </div>

                            <Controller
                                name="registration_start"
                                control={coreControl}
                                rules={{ required: t('cohort-create:validation.reg_start_required') }}
                                render={({ field }) => (
                                    <Input
                                        type="date"
                                        label={t('cohort-create:fields.registration_start_label')}
                                        error={coreErrors.registration_start?.message}
                                        required
                                        isDisabled={isCoreLocked}
                                        {...field}
                                    />
                                )}
                            />

                            <Controller
                                name="registration_end"
                                control={coreControl}
                                rules={{ required: t('cohort-create:validation.reg_end_required') }}
                                render={({ field }) => (
                                    <Input
                                        type="date"
                                        label={t('cohort-create:fields.registration_end_label')}
                                        error={coreErrors.registration_end?.message}
                                        required
                                        isDisabled={isCoreLocked}
                                        {...field}
                                        min={minRegEnd}
                                    />
                                )}
                            />

                            <Controller
                                name="start_date"
                                control={coreControl}
                                rules={{ required: t('cohort-create:validation.start_date_required') }}
                                render={({ field }) => (
                                    <Input
                                        type="date"
                                        label={t('cohort-create:fields.start_date_label')}
                                        error={coreErrors.start_date?.message}
                                        required
                                        isDisabled={isCoreLocked}
                                        {...field}
                                        min={minStartDate}
                                    />
                                )}
                            />

                            <Controller
                                name="status"
                                control={coreControl}
                                render={({ field }) => (
                                    <div className="flex items-center justify-between gap-2 px-4 py-3 rounded-lg border border-gray-200 bg-[var(--primary-canvas-color)]">
                                        <span className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)]">
                                            {t('cohort-create:fields.status_label')}
                                        </span>
                                        <Toggle
                                            active={field.value === 'ACTIVE'}
                                            onChange={(active) => field.onChange(active ? 'ACTIVE' : 'INACTIVE')}
                                            disabled={isCoreLocked}
                                        />
                                    </div>
                                )}
                            />
                        </div>

                        <div className="col-span-1 md:col-span-2 mt-4">
                            <label className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)] block mb-1">
                                Student Handbook (Optional)
                            </label>
                            <Controller
                                name="handbook_path"
                                control={coreControl}
                                render={({ field }) => {
                                    const [uploading, setUploading] = useState(false);
                                    
                                    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        
                                        const toastId = toast.loading(`Uploading "${file.name}"...`);
                                        try {
                                            setUploading(true);
                                            const res = await cohortsService.uploadFile(file);
                                            if (res.data?.filePath) {
                                                field.onChange(res.data.filePath);
                                                toast.success('Handbook uploaded successfully!', { id: toastId });
                                            } else {
                                                toast.error('Upload failed', { id: toastId });
                                            }
                                        } catch (err: any) {
                                            toast.error(err.response?.data?.message || err.message || 'Upload failed', { id: toastId });
                                        } finally {
                                            setUploading(false);
                                        }
                                    };

                                    return (
                                        <div className="flex flex-col gap-2 p-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                                            {field.value ? (
                                                <div className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-gray-200 shadow-xs">
                                                    <span className="text-xs font-medium text-gray-700 truncate max-w-md">
                                                        {typeof field.value === 'string' ? field.value.split('/').pop() : ''}
                                                    </span>
                                                    {!isCoreLocked && (
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className="text-red-500 hover:text-red-700 text-xs py-0 px-2 cursor-pointer"
                                                            onClick={() => field.onChange('')}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="file"
                                                        accept=".pdf,.doc,.docx"
                                                        id="handbook-upload"
                                                        className="hidden"
                                                        onChange={handleUpload}
                                                        disabled={uploading || isCoreLocked}
                                                    />
                                                    <label
                                                        htmlFor="handbook-upload"
                                                        className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-xs cursor-pointer ${
                                                            uploading || isCoreLocked ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                    >
                                                        Upload Handbook
                                                    </label>
                                                    <span className="text-xs text-gray-500">
                                                        PDF, DOC, DOCX up to 10MB
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        <div className="col-span-1 md:col-span-2 pt-4 border-t border-gray-200">
                            <label className="text-[12px] 2xl:text-[14px] font-bold text-[var(--secondary-color)] block mb-1">
                                Student Sidebar Modules Configuration
                            </label>
                            <span className="text-xs text-gray-500 block mb-3">
                                Configure which modules are enabled for fellows enrolled in this cohort.
                            </span>
                            <Controller
                                name="module_template_ids"
                                control={coreControl}
                                render={({ field }) => (
                                    <SidebarModulesSelector
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={isCoreLocked}
                                    />
                                )}
                            />

                            <div className="mt-4 max-w-sm">
                                <Controller
                                    name="student_portal_version"
                                    control={coreControl}
                                    render={({ field }) => (
                                        <Select
                                            label="Student Portal Experience"
                                            options={[
                                                { value: 'CLASSIC', label: 'Classic (Cohort 1 layout)' },
                                                { value: 'JOURNEY', label: 'Student Journey (Cohort 2+)' },
                                            ]}
                                            value={field.value}
                                            onChange={(v) => field.onChange(v || 'CLASSIC')}
                                            isClearable={false}
                                            isDisabled={isCoreLocked}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {!isCoreLocked && (
                            <div className="flex justify-end mt-4">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={updateCoreMutation.isPending}
                                >
                                    {updateCoreMutation.isPending ? 'Saving...' : 'Save Core Details'}
                                </Button>
                            </div>
                        )}
                    </FormSection>
                </form>

                {/* 2. Schedule Section */}
                <form onSubmit={handleWeekSubmit(onWeekSubmit)} noValidate>
                    <FormSection
                        title={t('cohort-create:sections.schedule')}
                        icon={<Icon name="schdulecalender" className="w-[12px] 2xl:w-[14px] h-auto" alt="" />}
                        isOpen={true}
                    >
                        {/* Week Navigation Tabs */}
                        <div className="flex border-b border-gray-200 mb-6 bg-gray-50 p-1.5 rounded-xl gap-2">
                            {[1, 2, 3, 4].map(wNum => {
                                const weekIsActive = (scheduleList.find(w => w.week_number === wNum)?.is_active) ?? true;
                                return (
                                    <button
                                        key={wNum}
                                        type="button"
                                        onClick={() => setActiveWeekTab(wNum)}
                                        className={`flex-1 py-2.5 text-center text-sm font-semibold rounded-lg transition-all duration-150 cursor-pointer flex items-center justify-center gap-1.5 ${
                                            activeWeekTab === wNum
                                                ? 'bg-white text-[var(--primary-color)] shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        Week {wNum}
                                        {!weekIsActive && (
                                            <span className="text-[10px] font-semibold text-gray-400" title="Hidden from students">(hidden)</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Week visibility toggle — always editable, independent of the content lock */}
                        <div className="flex items-center justify-between gap-2 px-4 py-3 mb-6 rounded-lg border border-gray-200 bg-[var(--primary-canvas-color)]">
                            <div className="flex flex-col">
                                <span className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)]">
                                    Week {activeWeekTab} Visibility
                                </span>
                                <span className="text-[11px] text-gray-500">
                                    {(activeWeekSchedule?.is_active ?? true) ? 'Visible to students in the portal' : 'Hidden from students in the portal'}
                                </span>
                            </div>
                            <Toggle
                                active={activeWeekSchedule?.is_active ?? true}
                                loading={visibilityUpdatingKey === 'week'}
                                onChange={(active) => {
                                    setVisibilityUpdatingKey('week');
                                    updateVisibilityMutation.mutate({ week_is_active: active });
                                }}
                            />
                        </div>

                        {/* Week Title Field */}
                        <div className="mb-6 bg-white p-4 border border-gray-200 rounded-xl shadow-xs">
                            <Controller
                                name="title"
                                control={weekControl}
                                render={({ field }) => (
                                    <Input
                                        label={`Week ${activeWeekTab} Title`}
                                        placeholder="Enter week title (e.g. Intro to Python, Capstone Project)"
                                        value={field.value || ''}
                                        onChange={field.onChange}
                                        error={weekErrors.title?.message}
                                        disabled={isCoreLocked}
                                    />
                                )}
                            />
                        </div>

                        {/* Days list inside active Week */}
                        <div className="flex flex-col gap-4">
                            {activeWeekSchedule?.days.map((_, dayIdx) => {
                                const dayNum = dayIdx + 1;
                                const isMentorSession = watchedDays[dayIdx]?.is_mentor_session;
                                const isDayOpen = !!openDays[dayNum];
                                const dateLabel = getDayDateLabel(dayNum);

                                return (
                                    <div
                                        key={`week-${activeWeekTab}-day-${dayNum}`}
                                        className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:border-gray-300 transition-colors"
                                    >
                                        <div
                                            onClick={() => toggleDayAccordion(dayNum)}
                                            className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer select-none"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--primary-surface-color)] text-[var(--primary-color)] text-sm font-bold shadow-inner">
                                                    D{dayNum}
                                                </span>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-[var(--secondary-color)]">
                                                        Day {dayNum} Class
                                                    </span>
                                                    {dateLabel && (
                                                        <span className="text-[11px] text-gray-500 font-medium">
                                                            {dateLabel}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {(() => {
                                                    const dayIsActive = activeWeekSchedule?.days[dayIdx]?.is_active ?? true;
                                                    return (
                                                        <span
                                                            className="flex items-center gap-1.5"
                                                            onClick={(e) => e.stopPropagation()}
                                                            title={dayIsActive ? 'Visible to students' : 'Hidden from students'}
                                                        >
                                                            <span className="text-[10px] font-semibold text-gray-400 hidden sm:inline">
                                                                {dayIsActive ? 'Visible' : 'Hidden'}
                                                            </span>
                                                            <Toggle
                                                                active={dayIsActive}
                                                                loading={visibilityUpdatingKey === `day-${dayNum}`}
                                                                onChange={(active) => {
                                                                    setVisibilityUpdatingKey(`day-${dayNum}`);
                                                                    updateVisibilityMutation.mutate({ days: [{ day_number: dayNum, is_active: active }] });
                                                                }}
                                                            />
                                                        </span>
                                                    );
                                                })()}
                                                <span className={`transition-transform duration-200 ${isDayOpen ? 'rotate-180' : ''}`}>
                                                    <Icon name="downarrow" className="w-[10px] h-auto" />
                                                </span>
                                            </div>
                                        </div>

                                        {isDayOpen && (
                                            <div className="p-4 bg-white grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100">
                                                <div className="col-span-1 md:col-span-2">
                                                    <Controller
                                                        name={`days.${dayIdx}.is_mentor_session`}
                                                        control={weekControl}
                                                        render={({ field }) => (
                                                            <div className="flex items-center gap-2 px-1 py-1">
                                                                <input
                                                                    type="checkbox"
                                                                    id={`days.${dayIdx}.is_mentor_session`}
                                                                    checked={!!field.value}
                                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                                    className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 cursor-pointer"
                                                                />
                                                                <label
                                                                    htmlFor={`days.${dayIdx}.is_mentor_session`}
                                                                    className="text-xs font-bold text-[var(--secondary-color)] cursor-pointer select-none"
                                                                >
                                                                    Mentor Session
                                                                </label>
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                                <Controller
                                                    name={`days.${dayIdx}.title`}
                                                    control={weekControl}
                                                    rules={{ required: t('cohort-create:validation.day_title_required') }}
                                                    render={({ field }) => (
                                                        <Input
                                                            label={t('cohort-create:fields.day_title_label')}
                                                            placeholder={t('cohort-create:fields.day_title_placeholder')}
                                                            error={weekErrors.days?.[dayIdx]?.title?.message}
                                                            required
                                                            {...field}
                                                            value={field.value || ''}
                                                        />
                                                    )}
                                                />

                                                {isMentorSession ? (
                                                    <div className="col-span-1 md:col-span-2 flex flex-col gap-4 p-4 border border-indigo-100 rounded-xl bg-indigo-50/20">
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-bold text-indigo-800 flex items-center gap-1.5">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-505 animate-pulse" />
                                                                Mentor Session Mode (Individual Student Links, Times & Descriptions)
                                                            </span>
                                                            <span className="text-[10px] text-gray-500 mt-0.5">
                                                                Provide a unique meeting link, time, and description for each student below. Left blank, the join button will be disabled.
                                                            </span>
                                                        </div>

                                                        {enrolledStudents.length === 0 ? (
                                                            <div className="text-center py-4 bg-white/50 border border-dashed border-gray-200 rounded-lg text-[11px] text-gray-400 font-semibold">
                                                                No students enrolled in this cohort yet.
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto pr-1">
                                                                {enrolledStudents.map((student: any) => {
                                                                    const studentName = `${student.user.first_name} ${student.user.last_name}`;
                                                                    const studentLinks = watchedDays[dayIdx]?.student_links || [];
                                                                    const linkObj = studentLinks.find((sl: any) => sl.enrollment_id === student.id);
                                                                    const currentLinkVal = linkObj?.link || '';
                                                                    const currentTimeVal = linkObj?.meeting_time || '';
                                                                    const currentDescVal = linkObj?.description || '';

                                                                    return (
                                                                        <div key={student.id} className="flex flex-col gap-4 p-4 bg-white border border-gray-100 rounded-xl shadow-2xs hover:border-gray-200 transition-all duration-200">
                                                                            <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
                                                                                <span className="text-xs font-black text-navy">{studentName}</span>
                                                                                <div className="flex items-center gap-1.5">
                                                                                    <span className="text-[10px] text-gray-400 font-semibold">Mentor:</span>
                                                                                    <select
                                                                                        value={student.mentor?.id || ''}
                                                                                        onChange={(e) => {
                                                                                            const mentorId = e.target.value;
                                                                                            if (mentorId) {
                                                                                                assignMentorMutation.mutate({ applicationId: student.application_id, mentorId });
                                                                                            }
                                                                                        }}
                                                                                        className="text-[10px] bg-transparent border-0 font-bold text-indigo-700 focus:ring-0 focus:outline-none cursor-pointer py-0 px-0.5 rounded hover:bg-indigo-50"
                                                                                    >
                                                                                        <option value="" disabled>Select Mentor</option>
                                                                                        {mentorsList.map((m: any) => (
                                                                                            <option key={m.id} value={m.id}>
                                                                                                {m.name}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </div>
                                                                            </div>
                                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                                <Input
                                                                                    label="Meeting Link"
                                                                                    placeholder="Enter meeting link (https://...)"
                                                                                    value={currentLinkVal}
                                                                                    onChange={(e) => {
                                                                                        const newLinkVal = e.target.value;
                                                                                        const currentLinks = [...(watchedDays[dayIdx]?.student_links || [])];
                                                                                        const existingIdx = currentLinks.findIndex((sl: any) => sl.enrollment_id === student.id);
                                                                                        if (existingIdx !== -1) {
                                                                                            currentLinks[existingIdx].link = newLinkVal;
                                                                                        } else {
                                                                                            currentLinks.push({ enrollment_id: student.id, link: newLinkVal, meeting_time: currentTimeVal, description: currentDescVal });
                                                                                        }
                                                                                        setWeekValue(`days.${dayIdx}.student_links`, currentLinks, { shouldDirty: true, shouldValidate: true });
                                                                                    }}
                                                                                />
                                                                                <Input
                                                                                    type="time"
                                                                                    label="Meeting Time"
                                                                                    placeholder="Time (e.g. 10:00 AM)"
                                                                                    value={currentTimeVal}
                                                                                    onChange={(e) => {
                                                                                        const newTimeVal = e.target.value;
                                                                                        const currentLinks = [...(watchedDays[dayIdx]?.student_links || [])];
                                                                                        const existingIdx = currentLinks.findIndex((sl: any) => sl.enrollment_id === student.id);
                                                                                        if (existingIdx !== -1) {
                                                                                            currentLinks[existingIdx].meeting_time = newTimeVal;
                                                                                        } else {
                                                                                            currentLinks.push({ enrollment_id: student.id, link: currentLinkVal, meeting_time: newTimeVal, description: currentDescVal });
                                                                                        }
                                                                                        setWeekValue(`days.${dayIdx}.student_links`, currentLinks, { shouldDirty: true, shouldValidate: true });
                                                                                    }}
                                                                                />
                                                                                <Input
                                                                                    label="Meeting Description (Optional)"
                                                                                    placeholder="Topic, notes or instructions"
                                                                                    value={currentDescVal}
                                                                                    onChange={(e) => {
                                                                                        const newDescVal = e.target.value;
                                                                                        const currentLinks = [...(watchedDays[dayIdx]?.student_links || [])];
                                                                                        const existingIdx = currentLinks.findIndex((sl: any) => sl.enrollment_id === student.id);
                                                                                        if (existingIdx !== -1) {
                                                                                            currentLinks[existingIdx].description = newDescVal;
                                                                                        } else {
                                                                                            currentLinks.push({ enrollment_id: student.id, link: currentLinkVal, meeting_time: currentTimeVal, description: newDescVal });
                                                                                        }
                                                                                        setWeekValue(`days.${dayIdx}.student_links`, currentLinks, { shouldDirty: true, shouldValidate: true });
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Controller
                                                        name={`days.${dayIdx}.link`}
                                                        control={weekControl}
                                                        rules={{
                                                            required: t('cohort-create:validation.day_link_required'),
                                                            pattern: {
                                                                value: /^https?:\/\/.+/,
                                                                message: t('cohort-create:validation.day_link_invalid'),
                                                            },
                                                        }}
                                                        render={({ field }) => (
                                                            <Input
                                                                label={t('cohort-create:fields.day_link_label')}
                                                                placeholder={t('cohort-create:fields.day_link_placeholder')}
                                                                error={weekErrors.days?.[dayIdx]?.link?.message}
                                                                required
                                                                {...field}
                                                                value={field.value || ''}
                                                            />
                                                        )}
                                                    />
                                                )}

                                                {!isMentorSession && (
                                                    <Controller
                                                        name={`days.${dayIdx}.class_time`}
                                                        control={weekControl}
                                                        rules={{ required: !isMentorSession ? 'Class time is required' : false }}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="time"
                                                                label="Class Time"
                                                                error={weekErrors.days?.[dayIdx]?.class_time?.message}
                                                                required
                                                                {...field}
                                                                value={field.value || ''}
                                                            />
                                                        )}
                                                    />
                                                )}

                                                <div className="col-span-1 md:col-span-2">
                                                    <Controller
                                                        name={`days.${dayIdx}.description`}
                                                        control={weekControl}
                                                        render={({ field }) => (
                                                            <Textarea
                                                                label={t('cohort-create:fields.day_description_label')}
                                                                placeholder={t('cohort-create:fields.day_description_placeholder')}
                                                                rows={2}
                                                                {...field}
                                                                value={field.value || ''}
                                                            />
                                                        )}
                                                    />
                                                </div>

                                                <Controller
                                                    name={`days.${dayIdx}.recorded_video`}
                                                    control={weekControl}
                                                    render={({ field }) => (
                                                        <Input
                                                            label={t('cohort-create:fields.recorded_video_label')}
                                                            placeholder={t('cohort-create:fields.recorded_video_placeholder')}
                                                            {...field}
                                                            value={field.value || ''}
                                                        />
                                                    )}
                                                />


                                                <div className="col-span-1 md:col-span-2 flex flex-col gap-4 p-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                                                     <div className="flex items-center justify-between">
                                                         <span className="text-xs font-semibold text-[var(--secondary-color)]">
                                                             Class Recordings (Multiple Uploads Allowed)
                                                         </span>
                                                         <span className="text-[10px] text-gray-400">
                                                             MP4, MOV, AVI, MKV formats (Bypasses server memory)
                                                         </span>
                                                     </div>

                                                     <Controller
                                                         name={`days.${dayIdx}.recordings`}
                                                         control={weekControl}
                                                         render={({ field: recordingsField }) => {
                                                             const recordingsList = recordingsField.value || [];

                                                             const [localVideoName, setLocalVideoName] = useState('');
                                                             const key = `edit-${activeWeekTab}-${dayIdx}`;
                                                             const isUploading = !!uploadingVideos[key];

                                                             const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                 const file = e.target.files?.[0];
                                                                 if (!file) return;

                                                                 const toastId = toast.loading(`Uploading video "${file.name}"...`);
                                                                 try {
                                                                     setUploadingVideos(prev => ({ ...prev, [key]: true }));
                                                                     const res = await cohortsService.uploadVideo(file, localVideoName || undefined);
                                                                     if (res.data?.filePath) {
                                                                         const newRecording = {
                                                                             path: res.data.filePath,
                                                                             name: localVideoName || res.data.fileName || file.name,
                                                                         };
                                                                         recordingsField.onChange([...recordingsList, newRecording]);
                                                                         setLocalVideoName('');
                                                                         toast.success('Video uploaded successfully!', { id: toastId });
                                                                     } else {
                                                                         toast.error('Upload failed', { id: toastId });
                                                                     }
                                                                 } catch (err: any) {
                                                                     toast.error(err.response?.data?.message || err.message || 'Upload failed', { id: toastId });
                                                                 } finally {
                                                                     setUploadingVideos(prev => ({ ...prev, [key]: false }));
                                                                 }
                                                             };

                                                             const removeRecording = (indexToRemove: number) => {
                                                                 const updated = recordingsList.filter((_, idx) => idx !== indexToRemove);
                                                                 recordingsField.onChange(updated);
                                                                 toast.success('Recording removed');
                                                             };

                                                             return (
                                                                 <div className="flex flex-col gap-3">
                                                                     {/* List of uploaded recordings */}
                                                                     {recordingsList.length > 0 && (
                                                                         <div className="flex flex-col gap-2 max-h-40 overflow-y-auto bg-white p-2 rounded-lg border border-gray-100">
                                                                             {recordingsList.map((rec: any, recIdx: number) => (
                                                                                 <div 
                                                                                     key={recIdx} 
                                                                                     className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-md border border-gray-100 shadow-2xs transition-colors"
                                                                                 >
                                                                                     <div className="flex flex-col min-w-0 pr-2">
                                                                                         <span className="text-xs font-semibold text-gray-800 truncate">
                                                                                             {rec.name}
                                                                                         </span>
                                                                                         <span className="text-[10px] text-gray-400 truncate">
                                                                                             {rec.path.split('/').pop()}
                                                                                         </span>
                                                                                     </div>
                                                                                     <Button
                                                                                         type="button"
                                                                                         variant="link"
                                                                                         className="text-red-500 hover:text-red-700 text-xs py-0 px-2 cursor-pointer"
                                                                                         onClick={() => removeRecording(recIdx)}
                                                                                     >
                                                                                         Remove
                                                                                     </Button>
                                                                                 </div>
                                                                             ))}
                                                                         </div>
                                                                     )}

                                                                     {/* Add new recording form */}
                                                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end bg-white/40 p-3 rounded-lg border border-gray-200/60">
                                                                         <div>
                                                                             <Input
                                                                                 label="Rename Video File (Optional)"
                                                                                 placeholder="Enter display name (e.g. Session 1 - Intro)"
                                                                                 value={localVideoName}
                                                                                 onChange={(e) => setLocalVideoName(e.target.value)}
                                                                             />
                                                                         </div>

                                                                         <div className="flex items-center gap-3">
                                                                             <input
                                                                                 type="file"
                                                                                 accept=".mp4,.mov,.avi,.mkv"
                                                                                 disabled={isUploading}
                                                                                 onChange={handleVideoUpload}
                                                                                 className="hidden"
                                                                                 id={`video-upload-input-${key}`}
                                                                             />
                                                                             <label
                                                                                 htmlFor={`video-upload-input-${key}`}
                                                                                 className={`w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 shadow-xs cursor-pointer transition-all duration-150 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                             >
                                                                                 {isUploading ? 'Uploading...' : 'Select & Upload Video'}
                                                                             </label>
                                                                         </div>
                                                                     </div>
                                                                 </div>
                                                             );
                                                         }}
                                                     />
                                                 </div>

                                                {/* Resource Attachments list */}
                                                <div className="col-span-1 md:col-span-2 border border-dashed border-gray-200 rounded-xl p-4 bg-gray-50/50">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <label className="text-xs font-semibold text-[var(--secondary-color)]">
                                                            {t('cohort-create:fields.attachments_label')}
                                                        </label>
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            onClick={() => addAttachment(dayIdx)}
                                                            className="text-xs py-1"
                                                        >
                                                            <Icon name="plus" className="w-3 h-auto mr-1" />
                                                            {t('cohort-create:buttons.add_attachment')}
                                                        </Button>
                                                    </div>

                                                    <Controller
                                                        name={`days.${dayIdx}.attachments`}
                                                        control={weekControl}
                                                        render={({ field }) => {
                                                            const items = field.value || [];
                                                            if (items.length === 0) {
                                                                return (
                                                                    <span className="text-xs text-gray-400 italic block py-1.5">
                                                                        No resource attachments added for this day.
                                                                    </span>
                                                                );
                                                            }
                                                            return (
                                                                <div className="flex flex-col gap-1.5">
                                                                    {items.map((file, fIdx) => {
                                                                        let parsed = { filePath: file, fileName: file, fileSize: 'N/A' };
                                                                        try {
                                                                            if (file.startsWith('{') && file.endsWith('}')) {
                                                                                parsed = JSON.parse(file);
                                                                            } else {
                                                                                parsed.fileName = file.split('/').pop()?.replace(/^\d+-/, '') || file;
                                                                            }
                                                                        } catch {}

                                                                        const fileUrl = parsed.filePath.startsWith('http') 
                                                                            ? parsed.filePath 
                                                                            : `${import.meta.env.VITE_API_BASE_URL ? import.meta.env.VITE_API_BASE_URL.replace(/\/api\/v1\/?$/, '') : ''}${parsed.filePath}`;

                                                                        return (
                                                                            <div
                                                                                key={fIdx}
                                                                                className="flex items-center justify-between px-3 py-1.5 bg-white border border-gray-100 rounded-lg text-xs"
                                                                            >
                                                                                <a
                                                                                    href={fileUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="font-semibold text-[var(--primary-color)] hover:underline truncate max-w-[80%]"
                                                                                >
                                                                                    {parsed.fileName} <span className="text-[10px] text-gray-400 font-normal">({parsed.fileSize})</span>
                                                                                </a>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => removeAttachment(dayIdx, fIdx)}
                                                                                    className="text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                                                                                >
                                                                                    <Icon name="close" className="w-3.5 h-auto" />
                                                                                </button>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-end mt-4">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={updateWeekMutation.isPending}
                            >
                                {updateWeekMutation.isPending ? 'Saving...' : `Save Week ${activeWeekTab} Schedule`}
                            </Button>
                        </div>
                    </FormSection>
                </form>
                </>
                )}

                {mainTab === 'coding' && (
                    <CodingTasksTab cohortId={id!} />
                )}

                {mainTab === 'attendance' && (
                    <AttendanceTab cohortId={id!} scheduleList={scheduleList} />
                )}

                {mainTab === 'evaluation' && (
                    <EvaluationGrid cohortId={id!} cohortName={cohort?.generated_cohort_title} />
                )}

                {/* Return button at bottom */}
                <div className="flex justify-end mt-4">
                    <Button
                        type="button"
                        variant="dark"
                        onClick={() => navigate('/admin/cohorts')}
                    >
                        Back to List
                    </Button>
                </div>
            </div>
        </>
    );
};

export default CohortDetailsPage;
